import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { Navigation, NavigationPageItem } from '@toolpad/core/AppProvider';
import { useModules } from '../api/endpoints/useModules';
import type { ModuleItem } from '../api/endpoints/useModules';
import {
  buildModulesNavigation,
  findFirstLeafPath,
  findMatchingLeaf,
  stripClientPrefix,
  type ModuleLeafRoute,
} from '../utils/modulesNavigation';
import { useNavigationTooltips } from './useNavigationTooltips';
import { useTitleHeaders } from './useTitleHeaders';
import { useActiveNavGroups } from './useActiveNavGroups';

const EMPTY_NAVIGATION_SIGNATURE = '[]';

function buildNavigationSignature(items: Navigation): string {
  const serialize = (entries: Navigation): Array<Record<string, unknown>> =>
    entries.map(entry => {
      if (entry.kind === 'header') {
        return { kind: 'header', title: entry.title };
      }
      if (entry.kind === 'divider') {
        return { kind: 'divider' };
      }

      const pageEntry = entry as NavigationPageItem;
      return {
        segment: pageEntry.segment,
        title: pageEntry.title,
        pattern: pageEntry.pattern,
        children: serialize(pageEntry.children ?? []),
      };
    });

  try {
    return JSON.stringify(serialize(items));
  } catch {
    return EMPTY_NAVIGATION_SIGNATURE;
  }
}

interface UseAppNavigationResult {
  navigation: Navigation;
  moduleLeaves: ModuleLeafRoute[];
  navigationSignature: string;
  firstLeafPath: string | null;
  activeToolpadPath: string | undefined;
  isModulesLoading: boolean;
}

export function useAppNavigation(clientId: string): UseAppNavigationResult {
  const location = useLocation();
  const { data: modulesResponse, isLoading: isModulesLoading } =
    useModules(clientId);

  const { navigation, leaves: moduleLeaves } = useMemo(() => {
    if (!clientId) {
      return {
        navigation: [] as Navigation,
        leaves: [],
      };
    }

    const rootItems =
      modulesResponse?.data && 'modules' in modulesResponse.data
        ? (modulesResponse.data as { modules?: ModuleItem[] }).modules
        : [];

    return buildModulesNavigation(rootItems, clientId);
  }, [modulesResponse?.data, clientId]);

  const navigationSignature = useMemo(
    () => buildNavigationSignature(navigation),
    [navigation]
  );

  const firstLeafPath = useMemo(
    () => findFirstLeafPath(moduleLeaves),
    [moduleLeaves]
  );

  useNavigationTooltips(navigationSignature);
  useTitleHeaders(navigationSignature);
  useActiveNavGroups({
    pathname: location.pathname,
    leaves: moduleLeaves,
    navigationSignature,
  });

  const activeToolpadPath = useMemo(() => {
    if (!moduleLeaves.length) {
      return undefined;
    }
    const clientless = stripClientPrefix(location.pathname);
    const match = findMatchingLeaf(clientless, moduleLeaves);
    return match?.toolpadPath;
  }, [location.pathname, moduleLeaves]);

  return {
    navigation,
    moduleLeaves,
    navigationSignature,
    firstLeafPath,
    activeToolpadPath,
    isModulesLoading,
  };
}
